<script setup lang="ts">
import { ref, useTemplateRef } from 'vue';
import type { OutputData } from '@editorjs/editorjs';
import NarrativeEditor from './NarrativeEditor.vue';
import type { Finding } from './findingLinkTool';

// Hidden, unlinked POC page (route /narratives/demo). Reachable only by
// typing the URL; auth-gated by the router's authGuard like every other
// route. This is a standalone demo of integrating the Editor.js block
// editor (now extracted into NarrativeEditor.vue, which also backs the real
// Narratives workspace).
//
// The headline feature proven here: select arbitrary text, click the
// "Link to finding" button in the inline toolbar, pick a finding from the
// dialog, and the selection becomes a special finding hyperlink. See
// findingLinkTool.ts and _planning/narrative_editor_poc.md.
//
// Everything is intentionally self-contained: hardcoded seed content,
// dummy findings, no persistence, no backend.

// ── Dummy findings (would come from the backend in the real feature) ──────
const FINDINGS: Finding[] = [
    { id: '7b3f', code: 'F-001', label: 'Breach of contract — late delivery' },
    { id: '9a1c', code: 'F-002', label: 'Misrepresentation in Q2 statements' },
    { id: 'c4d2', code: 'F-003', label: 'Unauthorized data access' },
    { id: 'e8f5', code: 'F-004', label: 'Failure to mitigate damages' },
    { id: '2k9p', code: 'F-005', label: 'Conflict of interest — board member' },
];

// Hardcoded sample narrative so the editor opens with realistic content.
const SEED_DATA: OutputData = {
    blocks: [
        {
            type: 'header',
            data: { text: 'Statement of Facts', level: 1 },
        },
        {
            type: 'paragraph',
            data: {
                text: 'This is a <i>proof-of-concept</i> narrative editor built on <b>Editor.js</b>. Select any phrase below, then click the link icon in the toolbar to attach it to a finding.',
            },
        },
        {
            type: 'header',
            data: { text: 'Background', level: 2 },
        },
        {
            type: 'paragraph',
            data: {
                text: 'On the morning in question, the vendor failed to deliver the goods on the agreed date, and the quarterly statements later proved inaccurate. Try selecting "failed to deliver the goods" and linking it to a finding.',
            },
        },
        {
            type: 'list',
            data: {
                style: 'unordered',
                items: [
                    'Select arbitrary text spanning a phrase or sentence.',
                    'Click the chain/link button that appears in the inline toolbar.',
                    'Pick a finding — the text becomes a finding hyperlink.',
                ],
            },
        },
        {
            type: 'quote',
            data: {
                text: 'The link is a TBD-formatted hyperlink encoding the finding id, so the narrative JSON carries the linkage for downstream rendering.',
                caption: 'POC rationale',
                alignment: 'left',
            },
        },
    ],
    version: '2.31.6',
    time: 0,
};

const editorRef = useTemplateRef<InstanceType<typeof NarrativeEditor>>('editorRef');
const savedJson = ref<string | null>(null);

const onShowJson = async () => {
    const data = await editorRef.value?.save();
    if (data) savedJson.value = JSON.stringify(data, null, 2);
};

const onResetDemo = () => {
    savedJson.value = null;
    void editorRef.value?.render(SEED_DATA);
};
</script>

<template>
    <main class="narrative-editor-page">
        <div class="narrative-editor-card">
            <header class="narrative-editor-head">
                <div>
                    <h1 class="narrative-editor-title">Narrative Editor — POC</h1>
                    <p class="narrative-editor-blurb">
                        Editor.js integration demo. Select text → click the link icon in the
                        toolbar → pick a finding. Content is hardcoded and not saved.
                    </p>
                </div>
                <div class="narrative-editor-actions">
                    <button type="button" class="narrative-btn" @click="onResetDemo">
                        Reset
                    </button>
                    <button
                        type="button"
                        class="narrative-btn narrative-btn-primary"
                        @click="onShowJson"
                    >
                        View saved JSON
                    </button>
                </div>
            </header>

            <NarrativeEditor ref="editorRef" :initial-data="SEED_DATA" :findings="FINDINGS" />

            <pre v-if="savedJson" class="narrative-editor-json">{{ savedJson }}</pre>
        </div>
    </main>
</template>

<style scoped>
.narrative-editor-page {
    display: flex;
    justify-content: center;
    padding: 32px 16px;
}

.narrative-editor-card {
    width: 100%;
    max-width: 820px;
    background: var(--color-bg-white);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-md);
    padding: 24px 28px;
}

.narrative-editor-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 12px;
}

.narrative-editor-title {
    margin: 0;
    font-size: var(--text-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
}

.narrative-editor-blurb {
    margin: 4px 0 0;
    color: var(--color-text-secondary);
    font-size: var(--text-sm);
    max-width: 60ch;
}

.narrative-editor-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
}

.narrative-btn {
    padding: 8px 14px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    background: var(--color-bg-white);
    color: var(--color-text-primary);
    cursor: pointer;
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
}

.narrative-btn:hover {
    background: var(--color-bg-subtle, #f4f4f5);
}

.narrative-btn-primary {
    background: var(--color-secondary);
    color: var(--color-bg-white);
    border-color: transparent;
}

.narrative-btn-primary:hover {
    background: var(--color-secondary-hover);
}

.narrative-editor-json {
    margin-top: 16px;
    padding: 16px;
    background: #1e1e1e;
    color: #d4d4d4;
    border-radius: var(--radius-md);
    font-size: 12px;
    line-height: 1.5;
    overflow: auto;
    max-height: 360px;
}
</style>
